import React, { useState } from "react";
import { Typography, TextField, IconButton, Stack, Box, Paper } from "@mui/material";
import { Edit, Check, Delete, Add } from "@mui/icons-material";
import PropTypes from "prop-types";

const PolicyNode = ({ node, onAdd, onEdit, onDelete, canEdit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(node.title);
  const level = node.number.split(".").length;
  const isTopLevel = level === 1;
  const isRootNode = level === 1 && node.number.split(".").length === 1;
  const maxCharLimit = Math.max(40, 80 - level * 10); // Decreases by 10 chars per level

  const handleSave = () => {
    onEdit(node.number, title);
    setIsEditing(false);
  };

  const handleTitleChange = (e) => {
    const input = e.target.value;
    setTitle(input);
  };

  return (
    <Paper
      elevation={level === 1 ? 1 : 0}
      sx={{
        ml: level > 1 ? 2 : 0, // Less indentation
        pl: level * 1.5, // Padding instead of margin for better text flow
        mb: 1,
        py: 0.5,
        backgroundColor: level === 1 ? "background.paper" : "transparent",
        borderLeft: level > 1 ? "2px solid" : "none",
        borderColor: level > 1 ? "divider" : "transparent",
        maxWidth: `calc(100% - ${level * 8}px)`, // Adjust width based on level
        overflowWrap: "break-word",
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Typography
          variant="body1"
          fontWeight={level === 1 ? "bold" : "normal"}
          sx={{
            whiteSpace: "nowrap",
            color: level === 1 ? "text.primary" : "text.secondary",
            minWidth: level === 1 ? "2.5rem" : "2rem", // Smaller for nested nodes
            fontSize: level > 2 ? "0.875rem" : "1rem", // Smaller text for deep nodes
          }}
        >
          {node.number}.
        </Typography>

        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            maxWidth: `calc(100% - ${level * 16}px)`, // Adjust based on level
          }}
        >
          {isEditing && !isRootNode ? (
            <TextField
              value={title}
              onChange={handleTitleChange}
              size="small"
              variant="outlined"
              disabled={!canEdit && canEdit !== undefined}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: level > 2 ? "0.75rem" : "0.875rem",
                  padding: "0.25rem 0.5rem",
                },
              }}
            />
          ) : (
            <Typography
              variant="body1"
              sx={{
                wordWrap: "break-word",
                whiteSpace: "pre-wrap",
                maxWidth: "100%",
                fontWeight: level === 1 ? "medium" : "normal",
                fontSize: level > 2 ? "0.875rem" : "1rem", // Smaller text for deep nodes
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={0} sx={{ opacity: isRootNode ? 0.5 : 1 }}>
          {isEditing ? (
            <IconButton
              onClick={handleSave}
              color="primary"
              size="small"
              sx={{ p: 0.25 }}
              disabled={isRootNode || (!canEdit && canEdit !== undefined)}
            >
              <Check fontSize={level > 2 ? "small" : "medium"} />
            </IconButton>
          ) : (
            !isTopLevel && (
              <IconButton
                onClick={() => setIsEditing(true)}
                color="primary"
                size="small"
                sx={{ p: 0.25 }}
                disabled={isRootNode || (!canEdit && canEdit !== undefined)}
              >
                <Edit fontSize={level > 2 ? "small" : "medium"} />
              </IconButton>
            )
          )}

          <IconButton
            onClick={() => onAdd(node.number)}
            color="success"
            size="small"
            disabled={!canEdit && canEdit !== undefined}
            sx={{ p: 0.25 }}
          >
            <Add fontSize={level > 2 ? "small" : "medium"} />
          </IconButton>

          {!isTopLevel && (
            <IconButton
              onClick={() => onDelete(node.number)}
              color="error"
              size="small"
              sx={{ p: 0.25 }}
              disabled={isRootNode || (!canEdit && canEdit !== undefined)}
            >
              <Delete fontSize={level > 2 ? "small" : "medium"} />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {node.children?.map((child) => (
        <PolicyNode
          key={child.number}
          node={child}
          onAdd={onAdd}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </Paper>
  );
};

PolicyNode.propTypes = {
  node: PropTypes.shape({
    number: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    children: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onAdd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export default PolicyNode;
